import { XMLParser } from "fast-xml-parser";

import { CountriesListQuery } from "src/core/gql/graphql";
import {
  Address,
  BirthDate,
  BirthPlace,
  DateType,
  EReputationMatchResult,
  GroupedCountryDetail,
  GroupedDate,
  GroupedName,
  GroupedRole,
  NameType,
  NameValue,
  PersonName,
  PersonNameEntry,
  PersonNamesGroupEntry,
  PersonProfile,
  RawAddressEntry,
  RawBirthPlaceEntry,
  RawCountryEntry,
  RawDateEntry,
  RawPersonNode,
  RawRelationEntry,
  RawRoleEntry,
  RawSanctionsReferenceEntry,
  RawWatchlistDescriptionEntry,
  Relation,
  Role,
  RoleType,
  WatchlistFlag,
} from "src/core/types/entity";
import { nonStandardCountryCodeMap } from "src/core/utils/country";

/**
 * Parse l'XML des résultats d'analyse e-reputation (version non détaillée).
 */
export function parseRawMatchResultsXml(
  xmlString: string,
  detailedResults: { content: string; positionInSearchResults: number }[],
  countriesList: CountriesListQuery
): EReputationMatchResult[] {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(`<root>${xmlString}</root>`, "application/xml");
  const matches = Array.from(xmlDoc.getElementsByTagName("match"));

  return matches.map((matchNode) => {
    const extractText = (tag: string, context: Element = matchNode): string | undefined => {
      return context.querySelector(tag)?.textContent ?? undefined;
    };

    const id = matchNode.getAttribute("peid") ?? "";

    // Score
    const rawScore = parseFloat(extractText("score") ?? "0") * 100;
    const score = Math.round(rawScore);

    // Primary name
    const primaryName = extractText("primary-name") ?? "";

    // Country code
    const countryCode = extractText("country-code") ?? "";
    let countryName = "";

    const country = countriesList.listElements
      .filter(
        (
          c
        ): c is {
          __typename: "Country";
          id: string;
          code: string;
          name: string;
          alpha3: string;
        } => c.__typename === "Country"
      )
      .find((c) => c.alpha3 === countryCode);
    if (country) {
      countryName = country.name;
    } else {
      countryName = nonStandardCountryCodeMap[countryCode] ?? "";
    }

    // Risk icons
    const riskIcons = Array.from(matchNode.querySelectorAll("risk-icon"))
      .map((el) => el.textContent)
      .filter((text): text is string => !!text && (text === "SAN" || text === "PEP"));

    // Dates of birth (on cherche la plus complète)
    const birthDateNodes = Array.from(matchNode.querySelectorAll("dates-of-birth > date-of-birth"));
    let dateOfBirth = "";
    for (const birthDateNode of birthDateNodes) {
      const year = extractText("year", birthDateNode);
      const month = extractText("month", birthDateNode);
      const day = extractText("day", birthDateNode);

      if (year && month && day) {
        dateOfBirth = `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`;
        break;
      } else if (year && month) {
        dateOfBirth ||= `${month.padStart(2, "0")}/${year}`;
      } else if (year) {
        dateOfBirth ||= `${year}`;
      }
    }

    const parser = new XMLParser({
      ignoreAttributes: false,
    });
    const matchingDetailedResult = detailedResults.find((detailedResult) => {
      const parsed = parser.parse(detailedResult.content);
      const person = parsed?.records.person;
      const peid = person?.["@_peid"];

      return peid === id;
    });

    const personProfile = matchingDetailedResult
      ? mapXmlToProfile(matchingDetailedResult.content)
      : mapXmlToProfile(""); // Fallback si pas de détails trouvés

    return {
      id,
      score,
      primaryName,
      countryCode,
      countryName,
      dateOfBirth,
      riskIcons,
      detail: personProfile,
    };
  });
}

/**
 * Normalise un tableau.
 * Si le tableau est undefined ou null, on retourne un tableau vide.
 * Sinon, on retourne le tableau.
 */
function normalizeArray<T>(entry: T | T[] | undefined | null): T[] {
  if (!entry) return [];
  return Array.isArray(entry) ? entry : [entry];
}

/**
 * Extrait les noms de la personne.
 */
const extractNames = (person: RawPersonNode): GroupedName[] => {
  const nameEntries = normalizeArray<PersonNameEntry>(
    person?.["person-name-details"]?.["person-names"] || []
  );

  const groupedNameMap: Record<NameType, PersonName[]> = {
    "Primary Name": [],
    "Also Known As": [],
    "Expanded Language Variation": [],
  };

  nameEntries.forEach((entry) => {
    const value = entry["person-name-value"];
    const nameValues = normalizeArray<NameValue>(value);

    nameValues.forEach((nameValue) => {
      const type = nameValue?.["name-type"];
      if (!type || !(type in groupedNameMap)) return;

      const baseName: PersonName = {
        firstName: nameValue?.["first-name"],
        middleName: nameValue?.["middle-name"],
        surname: nameValue?.["surname"],
      };

      const group = entry["person-names-group"]?.["person-names"];
      const originalScripts: PersonName["originalScripts"] = [];

      if (group) {
        normalizeArray<PersonNamesGroupEntry>(group).forEach((g) => {
          const osn = g["person-name-value"]?.["original-script-name"];
          if (osn) {
            const nameText = typeof osn === "string" ? osn : osn["#text"] ?? "";
            originalScripts.push({
              name: nameText,
              langCode: typeof osn === "object" ? osn["osn-language-code"] : undefined,
              scriptCode: typeof osn === "object" ? osn["script-language-code"] : undefined,
            });
          }
        });
      }

      if (originalScripts.length > 0) {
        baseName.originalScripts = originalScripts;
      }

      groupedNameMap[type].push(baseName);
    });
  });

  return Object.entries(groupedNameMap)
    .filter(([, list]) => list.length > 0)
    .map(([type, names]) => ({ type: type as NameType, names }));
};

/**
 * Extrait les dates de naissance, date de décès de la personne.
 */
const extractBirthDates = (person: RawPersonNode): GroupedDate[] => {
  const rawDates = normalizeArray<RawDateEntry>(person?.["date-details"]?.date || []);

  const raw: BirthDate[] = rawDates
    .filter((d) => d["date-type"])
    .map((d) => {
      const year = d.year;
      const month = d.month?.toString().padStart(2, "0");
      const day = d.day?.toString().padStart(2, "0");

      let formatted = "";
      if (year && month && day) formatted = `${year}-${month}-${day}`;
      else if (year && month) formatted = `${year}-${month}`;
      else if (year) formatted = year;

      return {
        date: formatted,
        type: d["date-type"] ?? "unknown",
      };
    })
    .filter((entry) => entry.date !== "");

  const grouped: Record<string, Set<string>> = {};
  raw.forEach((bd) => {
    if (!grouped[bd.type]) grouped[bd.type] = new Set();
    grouped[bd.type].add(bd.date);
  });

  return Object.entries(grouped).map(([type, dateSet]) => ({
    type: type as DateType,
    dates: Array.from(dateSet),
  }));
};

/**
 * Extrait les lieux de naissance de la personne.
 */
const extractBirthPlaces = (person: RawPersonNode): BirthPlace[] => {
  const birthPlaces = normalizeArray<RawBirthPlaceEntry>(
    person?.["birth-place-details"]?.["birth-place"] || []
  );

  return birthPlaces.map((bp) => {
    const countryNode = bp.country;
    return {
      place: bp["place-name"],
      country: typeof countryNode === "string" ? countryNode : countryNode?.["#text"] ?? "",
    };
  });
};

/**
 * Extrait les pays de la personne.
 */
const extractCountryDetails = (person: RawPersonNode): GroupedCountryDetail[] => {
  const raw = normalizeArray<RawCountryEntry>(person?.["country-details"]?.["country-value"] || []);

  const grouped: Record<string, Set<string>> = {};

  raw.forEach((entry) => {
    const type = entry["country-type"] ?? "unknown";
    const countryData = entry.country;

    const country =
      typeof countryData === "string"
        ? countryData
        : typeof countryData === "object"
        ? countryData["#text"] || ""
        : "";

    if (!grouped[type]) grouped[type] = new Set();
    grouped[type].add(country);
  });

  return Object.entries(grouped).map(([type, countries]) => ({
    type,
    countries: Array.from(countries),
  }));
};

/**
 * Extrait les adresses de la personne.
 */
const extractAddresses = (person: RawPersonNode): Address[] => {
  const addresses = normalizeArray<RawAddressEntry>(person?.["address-details"]?.address || []);

  return addresses.map((addr) => {
    const countryNode = addr.country;

    const country =
      typeof countryNode === "string"
        ? countryNode
        : typeof countryNode === "object"
        ? countryNode["#text"] ?? ""
        : "";

    const iso3 = typeof countryNode === "object" ? countryNode["iso3-country-code"] : undefined;

    return {
      addressLine: addr["address-line"],
      city: addr.city,
      region: addr.region,
      country,
      iso3,
    };
  });
};

/**
 * Formate les dates de début et de fin d'un rôle.
 */
const formatDateParts = (
  source: Record<string, unknown> | undefined,
  prefix: "since" | "to"
): string | undefined => {
  const y = source?.[`${prefix}-year`] as string | undefined;
  const m = (source?.[`${prefix}-month`] as string | number | undefined)
    ?.toString()
    .padStart(2, "0");
  const d = (source?.[`${prefix}-day`] as string | number | undefined)?.toString().padStart(2, "0");

  if (y && m && d) return `${y}-${m}-${d}`;
  if (y && m) return `${y}-${m}`;
  if (y) return y;
  return undefined;
};

/**
 * Extrait les rôles de la personne.
 */
const extractRoles = (person: RawPersonNode): GroupedRole[] => {
  const raw = normalizeArray<RawRoleEntry>(
    person?.["watchlist-content"]?.["role-details"]?.role || []
  ).map((role) => {
    const title = role["occupation-title"];

    let startDate: string | undefined = undefined;
    let endDate: string | undefined = undefined;
    let occupationCategory = "";
    let occupation = "";

    if (typeof title === "string") {
      occupation = title;
    } else if (typeof title === "object" && title !== null) {
      startDate = formatDateParts(title, "since");
      endDate = formatDateParts(title, "to");
      occupation = title["#text"] ?? "";
      occupationCategory = title["occupation-category"] ?? "";
    }

    return {
      type: role["role-type"] as RoleType,
      occupationCategory,
      occupation,
      startDate,
      endDate,
    };
  });

  const groupedMap: Record<RoleType, Role[]> = {
    "Primary Occupation": [],
    "Other Roles": [],
    "Previous Roles": [],
  };

  raw.forEach((role) => {
    groupedMap[role.type].push({
      occupationCategory: role.occupationCategory,
      occupation: role.occupation,
      startDate: role.startDate ?? "",
      endDate: role.endDate ?? "",
    });
  });

  const roleTypeOrder: Record<RoleType, number> = {
    "Primary Occupation": 0,
    "Other Roles": 1,
    "Previous Roles": 2,
  };

  return Object.entries(groupedMap)
    .sort(([a], [b]) => roleTypeOrder[a as RoleType] - roleTypeOrder[b as RoleType])
    .map(([type, roles]) => ({
      type: type as RoleType,
      roles: roles.sort((a, b) => (b.startDate || "").localeCompare(a.startDate || "")),
    }));
};

/**
 * Extrait les liste de surveillance de la personne.
 */
const extractWatchlist = (person: RawPersonNode): WatchlistFlag[] => {
  const result: WatchlistFlag[] = [];

  const descriptions = normalizeArray<RawWatchlistDescriptionEntry>(
    person?.["watchlist-content"]?.descriptions?.description || []
  );

  for (const desc of descriptions) {
    const desc1 = desc.description1 ?? "";
    const match = desc1.match(/\((.*?)\)/);
    if (match && match[1]) {
      result.push({ type: match[1], description: desc1 });
    }
  }

  const sanctions = normalizeArray<RawSanctionsReferenceEntry>(
    person?.["watchlist-content"]?.["sanctions-reference-details"]?.["sanctions-reference"] || []
  );

  if (sanctions.length > 0) {
    result.push({
      type: "SANCTION LIST",
      description: "Sanctions List (references found)",
    });
  }

  return result;
};

/**
 * Extrait les notes de la personne.
 */
const extractNotes = (person: RawPersonNode): string[] => {
  const note = person?.["watchlist-content"]?.["profile-notes"];
  return note ? [note] : [];
};

/**
 * Extrait les relations de la personne.
 */
const extractRelations = (person: RawPersonNode): Relation[] => {
  const raw = normalizeArray<RawRelationEntry>(person?.associates?.associate || []);

  return raw.map((a) => {
    const description1 = a.description1 ?? "";
    const match = description1.match(/\(([^)]+)\)/);

    const watchlist: WatchlistFlag[] = [];
    if (match && match[1]) {
      watchlist.push({
        type: match[1],
        description: description1,
      });
    }

    return {
      peid: a.peid ?? "",
      name: a["primary-name"] ?? "",
      relationship: a.relationship ?? "",
      watchlist,
    };
  });
};

/**
 * Traitement des données XML pour extraire les informations de la personne.
 * - Noms
 * - Dates de naissance
 * - Lieux de naissance
 * - Pays de naissance
 * - Adresses
 * - Rôles
 * - Sources
 * - Notes
 */
export function mapXmlToProfile(xml: string): PersonProfile {
  const parser = new XMLParser({ ignoreAttributes: false, attributeNamePrefix: "" });
  const parsed = parser.parse(xml);
  const person = parsed.records?.person;

  return {
    gender: person?.["gender"] ?? null,
    names: extractNames(person),
    birthDates: extractBirthDates(person),
    birthPlaces: extractBirthPlaces(person),
    countryDetails: extractCountryDetails(person),
    addresses: extractAddresses(person),
    images: normalizeArray(person["image-details"]?.["image-url"] || []),
    watchlist: extractWatchlist(person),
    roles: extractRoles(person),
    sources: normalizeArray(person?.["watchlist-content"]?.["source-details"]?.source || []),
    notes: extractNotes(person),
    relations: extractRelations(person),
  };
}
